import { delay } from 'redux-saga';
import { LOCATION_CHANGE } from 'react-router-redux';
import { call, put, select, take, all, takeLatest, fork, race } from 'redux-saga/effects';

import { sanitizeErrorMessage } from '@xanadu/ui-shared/utils/fetchService';

import { DISPLAY_MODAL, displayModal } from '@xanadu/ui-shared/modules/modal/actions';

import { LOGIN_SUCCESS, LOGOUT_SUCCESS } from '@xanadu/ui-shared/modules/userSession/actions';

import {
  REQUEST_CASINO_CONTROLS,
  setCasinoControls,
  setCasinoControlsErrors,
  UPDATE_CASINO_REALITY_CHECK,
  updateCasinoRealityCheckSuccess,
  updateCasinoRealityCheckErrors,
} from './actions';

import {
  fetchCasinoControls,
  submitCasinoRealityCheck,
} from './api';

function* handleRequestCasinoControls() {
  try {
    const { statusCode, errors, ...data } = yield call(fetchCasinoControls);
    if (statusCode === 200) {
      const { realityCheckMinutes, lossLimit = {} } = data;
      yield put(setCasinoControls({
        lossLimit,
        realityCheckMinutes,
      }));
    } else if (statusCode === 404) {
      yield put(setCasinoControls({
        lossLimit: {
          amount: null,
          periodDays: null,
        },
        realityCheckMinutes: null,
      }));
    } else {
      yield put(setCasinoControlsErrors(sanitizeErrorMessage(errors)));
    }
  } catch (e) {
    console.error('Error fetching casino controls ', e); // eslint-disable-line no-console
    yield put(setCasinoControlsErrors(e.message));
  }
}

function* handleUpdateCasinoRealityCheck(payload) {
  try {
    const { payload: load } = payload;
    const { realityCheckMinutes } = load;
    const existing = yield select(state => state.getIn(['casinoControls', 'realityCheckMinutes']));

    const { statusCode, errors, ...data } = yield call(submitCasinoRealityCheck, realityCheckMinutes, existing ? 'PUT' : 'POST');
    if (statusCode === 201 || statusCode === 200) {
      yield put(updateCasinoRealityCheckSuccess(data));
    } else {
      yield put(updateCasinoRealityCheckErrors(sanitizeErrorMessage(errors)));
    }
  } catch (e) {
    console.error('Error updating casino reality check ', e); // eslint-disable-line no-console
    yield put(updateCasinoRealityCheckErrors(e.message));
  }
}

function* handleUserSessionClean() {
  try {
    yield put(setCasinoControls({
      lossLimit: {
        amount: null,
        periodDays: null,
      },
      realityCheckMinutes: null,
    }));
  } catch (error) {
    console.error('Error clearing casino controls session ', error); // eslint-disable-line no-console
    yield put(setCasinoControlsErrors(sanitizeErrorMessage(error.message)));
  }
}

function* watchModalOverride() {
  // this is done to open again the 'casino-reality-check' modal if other one takes its place
  while (true) {
    yield take(LOGIN_SUCCESS);
    let loggedIn = yield select(state => state.getIn(['user', 'isLoggedIn']));
    while (loggedIn) {
      const { payload } = yield take(DISPLAY_MODAL);
      const { section, data } = payload;
      // we check for the correct modal
      if (section === 'casino-reality-check') {
        let haventDoneAnythingYet = true;
        while (haventDoneAnythingYet) {
          // in some cases we want to avoid open it again so we race for conditions
          const { modal, logout, page } = yield race({
            modal: take(DISPLAY_MODAL),
            logout: take(LOGOUT_SUCCESS),
            page: take(LOCATION_CHANGE),
          });
          if (logout || page) {
            haventDoneAnythingYet = false;
            if (logout) {
              loggedIn = false;
            }
          } else {
            const { payload: load } = modal;
            const { show: newShow } = load;
            const newSection = yield select(state => state.getIn(['modal', 'section']));
            // we don't want to open the modal again if the user just closed it
            if (!newShow && newSection !== 'casino-reality-check') {
              /*
              the reality check modal only opens in the game launch page
              if the user doesn't have a reality check value set
              and also if the user is moved to a different location within 150 ms from the modal close
              we are sure there was a redirect from a action button so we race again
              */
              const { location } = yield race({
                wait: delay(150),
                location: take(LOCATION_CHANGE),
              });
              const existing = yield select(state => state.getIn(['casinoControls', 'realityCheckMinutes']));
              if (!existing && !location) {
                // if we arrive here we can open the modal
                haventDoneAnythingYet = false;
                yield put(displayModal({
                  show: true,
                  section: 'casino-reality-check',
                  showCloseIcon: false,
                  data: {
                    ...data
                  },
                }));
              }
            }
          }
        }
      }
    }
  }
}

export default function* root() {
  yield all([
    takeLatest(REQUEST_CASINO_CONTROLS, handleRequestCasinoControls),
    takeLatest(UPDATE_CASINO_REALITY_CHECK, handleUpdateCasinoRealityCheck),
    takeLatest(LOGOUT_SUCCESS, handleUserSessionClean),
    fork(watchModalOverride),
  ]);
}
